/**
 * Pulse animation driver — ported from WM's DeckGLMap pulse machinery
 * (syncPulseAnimation / startPulseAnimation / canPulse). MIT, see
 * THIRD-PARTY-NOTICES.md.
 *
 * WM drives the pulse at a 500ms setInterval through the ordinary
 * (layer-cached) rebuild path instead of a RAF loop: cached families come
 * back reference-equal, so each tick only rebuilds the time-varying pulse
 * layer. Steady-state ticks skip while the browser has queued discrete
 * input so interaction handlers run first; the terminal tick — when the
 * pulse signal disappears — is never skipped, because it must commit the
 * static end state. No pulsing during the first 60s after startup.
 */

const PULSE_UPDATE_INTERVAL_MS = 500;
const PULSE_STARTUP_GRACE_MS = 60_000;

/**
 * Whether the browser has queued discrete input (click/keypress) waiting.
 * Chromium-only API; returns false where absent, degrading to no skip.
 */
function defaultIsInputPending(): boolean {
  const scheduling = (globalThis as unknown as {
    navigator?: { scheduling?: { isInputPending?: () => boolean } };
  }).navigator?.scheduling;
  if (typeof scheduling?.isInputPending !== 'function') return false;
  return scheduling.isInputPending();
}

export interface PulseDriverOptions {
  /** Whether anything on the map currently needs the pulse animation. */
  needsPulse: () => boolean;
  /** Request a (RAF-batched) layer rebuild. */
  requestRebuild: () => void;
  /** Injectable for tests; defaults to navigator.scheduling.isInputPending. */
  isInputPending?: () => boolean;
  /** Injectable clock for tests; defaults to Date.now. */
  now?: () => number;
}

export interface PulseDriver {
  /** Start or stop the interval based on the grace period and the pulse signal. */
  sync(): void;
  /** Tear down the interval (system teardown). */
  stop(): void;
  isRunning(): boolean;
}

export function createPulseDriver(options: PulseDriverOptions): PulseDriver {
  const isInputPending = options.isInputPending ?? defaultIsInputPending;
  const now = options.now ?? Date.now;
  const startupTime = now();

  let intervalId: ReturnType<typeof setInterval> | null = null;

  const stop = (): void => {
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };

  const start = (): void => {
    if (intervalId !== null) return;
    intervalId = setInterval(() => {
      if (!options.needsPulse()) {
        // Terminal tick: commit the static end state, never input-gated.
        stop();
        options.requestRebuild();
        return;
      }
      // Steady-state tick: skip when input is queued so the interaction
      // handler runs sooner. The pulse phase derives from the clock, so
      // no visual state is lost by skipping.
      if (!isInputPending()) options.requestRebuild();
    }, PULSE_UPDATE_INTERVAL_MS);
  };

  return {
    sync(): void {
      const canPulse = now() - startupTime > PULSE_STARTUP_GRACE_MS;
      const shouldPulse = canPulse && options.needsPulse();
      if (shouldPulse && intervalId === null) start();
      else if (!shouldPulse && intervalId !== null) stop();
    },
    stop,
    isRunning(): boolean {
      return intervalId !== null;
    },
  };
}
